import { delay, http, HttpResponse } from 'msw';

import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter';
import { creatorCollection } from '@/mocks/db/creator';

import {
  DiscoveryAssistantMode,
  DiscoveryUserMode,
} from '@/entities/discovery/discovery-constants';
import type {
  AnalyzeCommonalityRequest,
  AnalyzeCommonalityResponse,
  AnalyzePromptResponse,
  CreatePromptMessageRequest,
  CreateSearchModeMessageRequest,
  DiscoveryMessageListResponse,
  DiscoveryMessage,
  DiscoverySearchResponse,
  FilterConditions,
  ListCreatorDiscoverySearchResultsRequest,
  SearchHistoryResponse,
  SearchModeMessageResponse,
  SearchResultListResponse,
  SelectCommonalityRequest,
  SelectCommonalityResponse,
  UserCommonalityPostListItem,
  Creator,
} from '@/entities/discovery/api';

const BASE_URL = import.meta.env.VITE_SOCIAL_API_BASE_URL;
const MOCK_DISCOVERY_DELAY_MULTIPLIER = import.meta.env.MODE === 'test' ? 0 : 1;
const MOCK_DISCOVERY_DELAY_MS = 250;
const DEFAULT_SEARCH_LIMIT = 30;

const discoverySessions = new Map<
  string,
  {
    sessionId: string;
    workspaceId: string;
    currentSearchHistoryId: string | null;
    createdAt: string;
    updatedAt: string;
  }
>();
const discoveryMessages = new Map<string, DiscoveryMessage[]>();
const searchHistories = new Map<string, SearchHistoryResponse>();

const waitForDiscoveryMockDelay = () =>
  delay(MOCK_DISCOVERY_DELAY_MS * MOCK_DISCOVERY_DELAY_MULTIPLIER);

function getNowIso() {
  return new Date().toISOString();
}

function createMessageId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getDefaultFilterConditions(): FilterConditions {
  return {
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    end_date: getNowIso(),
    platform: [SOURCE_PLATFORM_TYPE.TIKTOK],
    demographics: null,
    engagement: null,
    official_account_only: null,
  };
}

function getDefaultPostFeatureConditions() {
  return ['skincare routine', 'product review', 'natural lighting'];
}

function getMockCommonalityPostList(postIds: string[]): UserCommonalityPostListItem[] {
  const matchedPosts = creatorCollection
    .findMany()
    .flatMap((creator) => creator.matched_posts ?? []);

  return postIds.map((postId) => {
    const matchedPost = matchedPosts.find((post) => post.id === postId);

    return {
      post_id: postId,
      poster_url:
        matchedPost?.poster_url ??
        `https://picsum.photos/seed/${encodeURIComponent(postId)}/416/720`,
    };
  });
}

function isSearchModeMessage(
  message: DiscoveryMessage | undefined,
): message is SearchModeMessageResponse {
  return message?.role === 'assistant' && message.assistant_mode === DiscoveryAssistantMode.SEARCH;
}

function hasKeywordSelectionContent(
  message: DiscoveryMessage | undefined,
): message is DiscoveryMessage & { content: { filter_conditions: FilterConditions } } {
  return (
    message?.role === 'assistant' &&
    message.assistant_mode === DiscoveryAssistantMode.KEYWORD_SELECTION &&
    'filter_conditions' in message.content
  );
}

function mapCreatorToSearchItem(
  creator: ReturnType<typeof creatorCollection.findMany>[number],
  searchId: string,
): Creator | null {
  return {
    search_id: searchId,
    creator_id: creator.creator_id,
    matched_keywords: creator.matched_keywords,
    matched_post_ids: creator.matched_post_ids ?? [],
    post_categories: creator.post_categories,
    is_saved: creator.is_saved,
    platform: creator.platform,
    username: creator.username,
    nickname: creator.nickname,
    profile_image_url: creator.profile_image_url,
    followers: creator.followers,
    average_er: creator.average_er,
    matched_posts: creator.matched_posts,
    excluded_date: creator.excluded_date,
    saved_at: creator.saved_at,
  };
}

function getSessionByWorkspaceId(workspaceId: string) {
  return Array.from(discoverySessions.values())
    .filter((session) => session.workspaceId === workspaceId)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0];
}

function createSession(workspaceId: string) {
  const now = getNowIso();
  const session = {
    sessionId: `mock-discovery-session-${workspaceId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    workspaceId,
    currentSearchHistoryId: null,
    createdAt: now,
    updatedAt: now,
  };

  discoverySessions.set(session.sessionId, session);
  discoveryMessages.set(session.sessionId, []);

  return session;
}

export function initMockDiscoverySessions(workspaceIds: string[] = []) {
  discoverySessions.clear();
  discoveryMessages.clear();
  searchHistories.clear();

  workspaceIds.forEach((workspaceId) => {
    createSession(workspaceId);
  });
}

function appendMessages(sessionId: string, messages: DiscoveryMessage[]) {
  const currentMessages = discoveryMessages.get(sessionId) ?? [];
  discoveryMessages.set(sessionId, [...currentMessages, ...messages]);
}

function createSearchModeMessage({
  sessionId,
  message,
  filterConditions = getDefaultFilterConditions(),
  postFeatureConditions = getDefaultPostFeatureConditions(),
  searchHistoryId = null,
}: {
  sessionId: string;
  message: string;
  filterConditions?: FilterConditions;
  postFeatureConditions?: string[];
  searchHistoryId?: string | null;
}): SearchModeMessageResponse {
  return {
    message_id: createMessageId('assistant-search'),
    discovery_session_id: sessionId,
    role: 'assistant',
    assistant_mode: DiscoveryAssistantMode.SEARCH,
    content: {
      message,
      search_conditions: {
        filter_conditions: filterConditions,
        post_feature_conditions: postFeatureConditions,
      },
    },
    search_history_id: searchHistoryId,
    created_at: getNowIso(),
  };
}

function createAnalyzePromptAssistantMessage({
  sessionId,
  prompt,
}: {
  sessionId: string;
  prompt: string;
}): AnalyzePromptResponse['assistant_message'] {
  const trimmedPrompt = prompt.trim();
  const filterConditions = getDefaultFilterConditions();

  if (trimmedPrompt === '1') {
    return createSearchModeMessage({
      sessionId,
      message: '1번 입력에 대해 검색 조건을 구성했습니다.',
      filterConditions,
    });
  }

  if (trimmedPrompt === '2') {
    return {
      message_id: createMessageId('assistant-suggestion'),
      discovery_session_id: sessionId,
      role: 'assistant',
      assistant_mode: DiscoveryAssistantMode.SUGGESTION,
      content: {
        message: '2번 입력에 대해 바로 사용할 수 있는 프롬프트를 제안합니다.',
        suggested_prompts: [
          '비건 스킨케어 루틴을 자연스럽게 소개하는 크리에이터',
          '제품 리뷰와 사용 전후 비교 콘텐츠를 자주 올리는 크리에이터',
          '틱톡에서 뷰티 팁과 튜토리얼 반응이 좋은 크리에이터',
        ],
      },
      created_at: getNowIso(),
    };
  }

  if (trimmedPrompt === '3') {
    return {
      message_id: createMessageId('assistant-failure'),
      discovery_session_id: sessionId,
      role: 'assistant',
      assistant_mode: DiscoveryAssistantMode.FAILURE,
      content: {
        message: '3번 입력은 검색 조건으로 변환할 수 없습니다.',
        failure_reason:
          '요청에서 크리에이터 검색에 필요한 대상, 콘텐츠 특징, 플랫폼 단서를 찾지 못했습니다.',
      },
      created_at: getNowIso(),
    };
  }

  if (trimmedPrompt === '4') {
    return {
      message_id: createMessageId('assistant-answer'),
      discovery_session_id: sessionId,
      role: 'assistant',
      assistant_mode: DiscoveryAssistantMode.ANSWER,
      content: {
        message:
          '4번 입력에 대한 안내 답변입니다. 더 구체적인 타깃이나 콘텐츠 특징을 입력하면 검색 조건을 만들 수 있습니다.',
      },
      created_at: getNowIso(),
    };
  }

  if (trimmedPrompt === '5') {
    return {
      message_id: createMessageId('assistant-keyword-selection'),
      discovery_session_id: sessionId,
      role: 'assistant',
      assistant_mode: DiscoveryAssistantMode.KEYWORD_SELECTION,
      content: {
        message: '5번 입력에 대해 선택 가능한 콘텐츠 특징 후보를 찾았습니다.',
        filter_conditions: filterConditions,
        candidate_post_feature_conditions: [
          'skincare routine',
          'product review',
          'before after demo',
          'creator voiceover',
          'natural lighting',
        ],
        selected_post_feature_conditions: null,
      },
      created_at: getNowIso(),
    };
  }

  return createSearchModeMessage({
    sessionId,
    message: `${prompt} 조건으로 검색할 준비가 됐습니다.`,
    filterConditions,
  });
}

function createSearchHistory({
  sessionId,
  searchId,
  prompt,
  filterConditions,
  seedPostIds,
}: {
  sessionId: string;
  searchId: string;
  prompt: string | null;
  filterConditions: FilterConditions;
  seedPostIds?: string[];
}) {
  const session = discoverySessions.get(sessionId);
  const creators = creatorCollection.findMany();
  const history: SearchHistoryResponse = {
    id: searchId,
    workspace_id: session?.workspaceId ?? 'mock-workspace-id',
    search_type: seedPostIds != null && seedPostIds.length > 0 ? 'lookalike' : 'prompt',
    prompt,
    filters: filterConditions,
    post_feature_conditions: getDefaultPostFeatureConditions(),
    seed_post_ids: seedPostIds ?? null,
    complete: true,
    progress_percentage: 100,
    total_count: creators.length,
    last_search_attempt: 1,
    created_at: getNowIso(),
  };

  searchHistories.set(searchId, history);

  if (session != null) {
    session.currentSearchHistoryId = searchId;
    session.updatedAt = getNowIso();
  }

  return history;
}

function getSearchHistoryKey(sessionId: string, searchId: string) {
  return `${sessionId}:${searchId}`;
}

function getVisibleCreators(searchId: string, request: ListCreatorDiscoverySearchResultsRequest) {
  const showSaved = request.show_saved === true;

  return creatorCollection
    .findMany()
    .filter(
      (creator) => creator.search_id === searchId || searchId.startsWith('mock-discovery-search'),
    )
    .filter((creator) => showSaved || !creator.is_saved);
}

export const discoveryHandlers = [
  http.get(`${BASE_URL}/workspaces/:workspaceId/discovery-session`, async ({ params }) => {
    await waitForDiscoveryMockDelay();

    const workspaceId = String(params.workspaceId);
    const session = getSessionByWorkspaceId(workspaceId);

    if (session == null) {
      return new HttpResponse(null, { status: 204 });
    }

    return HttpResponse.json({
      session_id: session.sessionId,
      workspace_id: session.workspaceId,
      current_search_history_id: session.currentSearchHistoryId,
      created_at: session.createdAt,
      updated_at: session.updatedAt,
    });
  }),

  http.post(`${BASE_URL}/workspaces/:workspaceId/discovery-session`, async ({ params }) => {
    await waitForDiscoveryMockDelay();

    const session = createSession(String(params.workspaceId));

    return HttpResponse.json({ session_id: session.sessionId });
  }),

  http.get(`${BASE_URL}/discovery-sessions/:sessionId/messages`, async ({ params, request }) => {
    await waitForDiscoveryMockDelay();

    const sessionId = String(params.sessionId);
    const url = new URL(request.url);
    const limit = Number(url.searchParams.get('limit')) || 50;
    const cursor = url.searchParams.get('cursor');
    const messages = discoveryMessages.get(sessionId) ?? [];
    const startIndex =
      cursor == null
        ? 0
        : Math.max(messages.findIndex((message) => message.message_id === cursor) + 1, 0);
    const paginatedMessages = messages.slice(startIndex, startIndex + limit);
    const nextMessage = messages[startIndex + limit] ?? null;
    const response: DiscoveryMessageListResponse = {
      messages: paginatedMessages,
      next_cursor: nextMessage?.message_id ?? null,
      has_more: nextMessage != null,
    };

    return HttpResponse.json(response);
  }),

  http.post(
    `${BASE_URL}/discovery-sessions/:sessionId/analyze-prompt`,
    async ({ params, request }) => {
      await waitForDiscoveryMockDelay();

      const sessionId = String(params.sessionId);
      const body = (await request.json()) as CreatePromptMessageRequest;
      const userMessage: AnalyzePromptResponse['user_message'] = {
        message_id: createMessageId('user-prompt'),
        discovery_session_id: sessionId,
        role: 'user',
        user_mode: DiscoveryUserMode.PROMPT,
        content: { message: body.message },
        created_at: getNowIso(),
      };
      const assistantMessage = createAnalyzePromptAssistantMessage({
        sessionId,
        prompt: body.message,
      });
      const response: AnalyzePromptResponse = {
        user_message: userMessage,
        assistant_message: assistantMessage,
      };

      appendMessages(sessionId, [userMessage, assistantMessage]);

      return HttpResponse.json(response);
    },
  ),

  http.post(
    `${BASE_URL}/discovery-sessions/:sessionId/search-mode-message`,
    async ({ params, request }) => {
      await waitForDiscoveryMockDelay();

      const sessionId = String(params.sessionId);
      const body = (await request.json()) as CreateSearchModeMessageRequest;
      const messages = discoveryMessages.get(sessionId) ?? [];
      const sourceMessage = messages.find((message) => message.message_id === body.message_id);
      const assistantMessage = createSearchModeMessage({
        sessionId,
        message: isSearchModeMessage(sourceMessage)
          ? sourceMessage.content.message
          : 'mock discovery search',
        filterConditions: isSearchModeMessage(sourceMessage)
          ? sourceMessage.content.search_conditions.filter_conditions
          : getDefaultFilterConditions(),
        postFeatureConditions: isSearchModeMessage(sourceMessage)
          ? sourceMessage.content.search_conditions.post_feature_conditions
          : getDefaultPostFeatureConditions(),
      });

      appendMessages(sessionId, [assistantMessage]);

      return HttpResponse.json(assistantMessage);
    },
  ),

  http.post(`${BASE_URL}/discovery-sessions/:sessionId/search`, async ({ params, request }) => {
    await waitForDiscoveryMockDelay();

    const sessionId = String(params.sessionId);
    const body = (await request.json()) as { message_id: string };
    const messages = discoveryMessages.get(sessionId) ?? [];
    const sourceMessage = messages.find((message) => message.message_id === body.message_id);
    const filterConditions = isSearchModeMessage(sourceMessage)
      ? sourceMessage.content.search_conditions.filter_conditions
      : getDefaultFilterConditions();
    const searchId = `mock-discovery-search-${Date.now()}`;

    createSearchHistory({
      sessionId,
      searchId,
      prompt: isSearchModeMessage(sourceMessage)
        ? sourceMessage.content.message
        : 'mock discovery search',
      filterConditions,
    });

    const response: DiscoverySearchResponse = {
      search_id: searchId,
      complete: true,
      progress_percentage: 100,
    };

    return HttpResponse.json(response);
  }),

  http.get(
    `${BASE_URL}/discovery-sessions/:sessionId/search/:searchId/status`,
    async ({ params }) => {
      await waitForDiscoveryMockDelay();

      const sessionId = String(params.sessionId);
      const searchId = String(params.searchId);
      const history =
        searchHistories.get(searchId) ??
        searchHistories.get(getSearchHistoryKey(sessionId, searchId)) ??
        createSearchHistory({
          sessionId,
          searchId,
          prompt: 'mock discovery search',
          filterConditions: getDefaultFilterConditions(),
        });

      return HttpResponse.json(history);
    },
  ),

  http.get(
    `${BASE_URL}/discovery-sessions/:sessionId/search/:searchId/results`,
    async ({ params, request }) => {
      await waitForDiscoveryMockDelay();

      const searchId = String(params.searchId);
      const url = new URL(request.url);
      const searchRequest: ListCreatorDiscoverySearchResultsRequest = {
        attempt: Number(url.searchParams.get('attempt')) || 1,
        show_saved: url.searchParams.get('show_saved') === 'true',
      };
      const startIndex = (searchRequest.attempt - 1) * DEFAULT_SEARCH_LIMIT;
      const creators = getVisibleCreators(searchId, searchRequest);
      const paginatedCreators = creators.slice(startIndex, startIndex + DEFAULT_SEARCH_LIMIT);
      const response: SearchResultListResponse = {
        complete: startIndex + DEFAULT_SEARCH_LIMIT >= creators.length,
        total_count: creators.length,
        creators: paginatedCreators
          .map((creator) => mapCreatorToSearchItem(creator, searchId))
          .filter((creator): creator is Creator => creator != null),
      };

      return HttpResponse.json(response);
    },
  ),

  http.post(
    `${BASE_URL}/discovery-sessions/:sessionId/analyze-commonality`,
    async ({ params, request }) => {
      await waitForDiscoveryMockDelay();

      const sessionId = String(params.sessionId);
      const body = (await request.json()) as AnalyzeCommonalityRequest;
      const messages = discoveryMessages.get(sessionId) ?? [];
      const latestSearchMessage = messages.filter(isSearchModeMessage).at(-1);
      const filterConditions =
        latestSearchMessage?.content.search_conditions.filter_conditions ??
        getDefaultFilterConditions();
      const userMessage: AnalyzeCommonalityResponse['user_message'] = {
        message_id: createMessageId('user-posts'),
        discovery_session_id: sessionId,
        role: 'user',
        user_mode: DiscoveryUserMode.POSTS,
        content: {
          post_list: getMockCommonalityPostList(body.post_id_list),
          filter_conditions: filterConditions,
        },
        created_at: getNowIso(),
      };
      const assistantMessage: AnalyzeCommonalityResponse['assistant_message'] = {
        message_id: createMessageId('assistant-keyword-selection'),
        discovery_session_id: sessionId,
        role: 'assistant',
        assistant_mode: DiscoveryAssistantMode.KEYWORD_SELECTION,
        content: {
          message: '선택한 게시물에서 공통 콘텐츠 특징을 찾았습니다.',
          filter_conditions: filterConditions,
          candidate_post_feature_conditions: [
            'skincare routine',
            'ingredient review',
            'before after demo',
            'creator voiceover',
            'natural lighting',
          ],
          selected_post_feature_conditions: null,
        },
        created_at: getNowIso(),
      };
      const response: AnalyzeCommonalityResponse = {
        user_message: userMessage,
        assistant_message: assistantMessage,
      };

      appendMessages(sessionId, [userMessage, assistantMessage]);

      return HttpResponse.json(response);
    },
  ),

  http.post(
    `${BASE_URL}/discovery-sessions/:sessionId/select-commonality`,
    async ({ params, request }) => {
      await waitForDiscoveryMockDelay();

      const sessionId = String(params.sessionId);
      const body = (await request.json()) as SelectCommonalityRequest;
      const messages = discoveryMessages.get(sessionId) ?? [];
      const keywordSelectionMessage = messages.find(
        (message) =>
          message.message_id === body.message_id &&
          message.assistant_mode === DiscoveryAssistantMode.KEYWORD_SELECTION,
      );
      const filterConditions = hasKeywordSelectionContent(keywordSelectionMessage)
        ? keywordSelectionMessage.content.filter_conditions
        : getDefaultFilterConditions();
      const searchId = `mock-discovery-search-${Date.now()}`;
      const assistantMessage = createSearchModeMessage({
        sessionId,
        message: '선택한 공통 특징으로 유사 크리에이터를 검색할 준비가 됐습니다.',
        filterConditions,
        searchHistoryId: searchId,
      });
      const history = createSearchHistory({
        sessionId,
        searchId,
        prompt: null,
        filterConditions,
        seedPostIds: body.selected_post_feature_conditions,
      });
      const response: SelectCommonalityResponse = {
        ...assistantMessage,
        keyword_selection_message_id: body.message_id,
        search_history_id: history.id,
      };

      appendMessages(sessionId, [response]);

      return HttpResponse.json(response);
    },
  ),
];
